import { skipToken } from '@reduxjs/toolkit/query';
import { Menu, MenuItem } from '@shared/ui';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { shouldRequireGoogleDriveForUser } from '../../platform/auth/userRoles';
import {
  useGetGoogleConnectionStatusQuery,
  useGetProviderConnectionsQuery,
  useRevokeProviderOAuthMutation,
  useStartGoogleOAuthMutation,
} from '../../store/api/apiSlice';
import { useAppSelector } from '../../store/hooks';
import GoogleDriveRow from './components/GoogleDriveRow';
import { hasConnectedProviderConnection } from './providerConnectionStatus';

const DRIVE_PROVIDER = 'google-drive';
const DRIVE_DEFAULT_SCOPES = ['https://www.googleapis.com/auth/drive.readonly'];

const DriveConnectionTile: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user);
  const personId = user?.id;
  const authToken = useAppSelector((s) => s.auth.token);

  const { data: googleStatus } = useGetGoogleConnectionStatusQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: !authToken,
  });
  const { data: drvConns } = useGetProviderConnectionsQuery(
    personId ? { provider: 'google-drive', personId } : (skipToken as any)
  );

  const [startGoogleOAuth] = useStartGoogleOAuthMutation();
  const [revokeProviderOAuth] = useRevokeProviderOAuthMutation();
  const migrationRequired =
    shouldRequireGoogleDriveForUser(user) &&
    (googleStatus?.legacy_providers || []).some(
      (provider) => (provider || '').toLowerCase() === DRIVE_PROVIDER
    );

  const connected = hasConnectedProviderConnection(drvConns?.connections);

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget);
  const closeMenu = () => setAnchor(null);

  const handleConnect = async () => {
    closeMenu();
    try {
      const response = await startGoogleOAuth({
        provider: DRIVE_PROVIDER,
        scopes: DRIVE_DEFAULT_SCOPES,
        redirect: '/settings/integrations',
      }).unwrap();
      window.location.href = response.auth_url;
    } catch {
      // noop
    }
  };

  const handleDisconnect = async () => {
    closeMenu();
    if (!personId) return;
    try {
      await revokeProviderOAuth({ personId, provider: 'google-drive' }).unwrap();
    } catch {
      // noop
    }
  };

  return (
    <>
      <GoogleDriveRow
        to={connected && !migrationRequired ? '/settings/integrations/google-drive' : undefined}
        connected={connected}
        migrationRequired={migrationRequired}
        iconSrc={'https://ssl.gstatic.com/images/branding/product/2x/drive_48dp.png'}
        email={
          migrationRequired
            ? 'Reconnect to move Drive to the new Google client'
            : googleStatus?.email
        }
        onOpenSettings={() => {
          if (!connected || migrationRequired) {
            void handleConnect();
          }
        }}
        onOpenMenu={openMenu}
      />
      <Menu anchorEl={anchor} open={!!anchor} onClose={closeMenu}>
        {connected ? (
          <MenuItem onClick={closeMenu}>
            <Link
              to="/settings/integrations/google-drive"
              style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%' }}
            >
              Settings
            </Link>
          </MenuItem>
        ) : null}
        {!connected || migrationRequired ? (
          <MenuItem onClick={handleConnect}>{migrationRequired ? 'Reconnect' : 'Connect'}</MenuItem>
        ) : (
          <MenuItem onClick={handleDisconnect}>Disconnect</MenuItem>
        )}
      </Menu>
    </>
  );
};

export default DriveConnectionTile;
